import { parsePartnerDateParts, toPartnerIsoDate } from './partnerDateUtils';
import { getPaeQueueDisplayStatus } from './partnerProjectStatusUtils';

export const PARTNER_TABLE_DATE_FILTER_KEYS = new Set([
  'estStart',
  'estGolive',
  'actStart',
  'actGolive',
  'start',
  'golive',
]);
export const PARTNER_TABLE_EXACT_FILTER_KEYS = new Set(['pae', 'region']);
export const PARTNER_TABLE_MULTI_SELECT_FILTER_KEYS = new Set(['status']);

export const normalizePartnerTableMultiFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }

  const normalized = String(value || '').trim();
  return normalized ? [normalized] : [];
};

export const isPartnerTableFilterValueActive = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return Boolean(String(value || '').trim());
};

export const parsePartnerServiceNumber = (value) => {
  const match = String(value || '').trim().match(/^PS-(\d+)$/i);
  return match ? Number(match[1]) : null;
};

export const getPartnerTableSortTimestamp = (value) => {
  if (!value) {
    return null;
  }

  const parts = parsePartnerDateParts(value);
  if (parts) {
    return new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day)).getTime();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

export const comparePartnerTableValues = (left, right, direction = 'asc') => {
  const normalize = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return value;
  };

  const leftValue = normalize(left);
  const rightValue = normalize(right);

  if (leftValue === null && rightValue === null) {
    return 0;
  }
  if (leftValue === null) {
    return 1;
  }
  if (rightValue === null) {
    return -1;
  }

  let result = 0;
  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    result = leftValue - rightValue;
  } else {
    result = String(leftValue).localeCompare(String(rightValue), undefined, { sensitivity: 'base' });
  }

  return direction === 'desc' ? -result : result;
};

export const applyPartnerTableColumnFilters = (projects, columnFilters, getColumnValue) => {
  const activeFilters = Object.entries(columnFilters || {}).filter(
    ([, value]) => isPartnerTableFilterValueActive(value),
  );

  if (!activeFilters.length) {
    return projects;
  }

  return projects.filter((project) => activeFilters.every(([columnKey, filterValue]) => {
    if (PARTNER_TABLE_MULTI_SELECT_FILTER_KEYS.has(columnKey)) {
      const selectedValues = normalizePartnerTableMultiFilterValue(filterValue);
      if (!selectedValues.length) {
        return true;
      }

      const cellValue = getColumnValue(project, columnKey);
      const normalizedCell = String(cellValue || '').toLowerCase();
      return selectedValues.some(
        (selected) => selected.toLowerCase() === normalizedCell,
      );
    }

    const normalizedFilter = String(filterValue).trim().toLowerCase();
    if (!normalizedFilter) {
      return true;
    }

    if (columnKey === 'products') {
      const products = project?.productsInScope || [];
      return products.some(
        (product) => String(product || '').trim().toLowerCase() === normalizedFilter,
      );
    }

    const cellValue = getColumnValue(project, columnKey);

    if (PARTNER_TABLE_DATE_FILTER_KEYS.has(columnKey)) {
      const filterIso = toPartnerIsoDate(filterValue);
      if (!filterIso) {
        return true;
      }
      return toPartnerIsoDate(cellValue) === filterIso;
    }

    if (PARTNER_TABLE_EXACT_FILTER_KEYS.has(columnKey)) {
      return String(cellValue || '').toLowerCase() === normalizedFilter;
    }

    return String(cellValue || '').toLowerCase().includes(normalizedFilter);
  }));
};

export const applyPartnerTableSort = (
  projects,
  sortKey,
  sortDirection,
  getColumnValue,
  getSortValue,
) => {
  if (!sortKey || !sortDirection) {
    return projects;
  }

  const sortedProjects = [...projects];
  sortedProjects.sort((leftProject, rightProject) => {
    const leftValue = getSortValue
      ? getSortValue(leftProject, sortKey)
      : getColumnValue(leftProject, sortKey);
    const rightValue = getSortValue
      ? getSortValue(rightProject, sortKey)
      : getColumnValue(rightProject, sortKey);

    return comparePartnerTableValues(leftValue, rightValue, sortDirection);
  });

  return sortedProjects;
};

export const getMyProjectsColumnValue = (project, columnKey) => {
  switch (columnKey) {
    case 'customer':
      return project.customerName || '';
    case 'companyName':
      return project.companyName || '';
    case 'products':
      return (project.productsInScope || []).join(' ');
    case 'engagement':
      return project.engagementType || '';
    case 'estStart':
    case 'start':
      return project.estimatedStartDate || '';
    case 'estGolive':
    case 'golive':
      return project.estimatedGoliveDate || '';
    case 'actStart':
      return project.actualStartDate || '';
    case 'actGolive':
      return project.actualGoliveDate || '';
    case 'submitted':
      return project.partnerName || project.submittedBy || '';
    case 'status':
      return project.projectStatus || '';
    case 'pae':
      return project.partnerAdoptionEngineer || '';
    case 'region':
      return project.region || '';
    default:
      return '';
  }
};

export const getMyProjectsSortValue = (project, columnKey) => {
  if (columnKey === 'estStart' || columnKey === 'start') {
    return getPartnerTableSortTimestamp(project.estimatedStartDate);
  }
  if (columnKey === 'estGolive' || columnKey === 'golive') {
    return getPartnerTableSortTimestamp(project.estimatedGoliveDate);
  }
  if (columnKey === 'actStart') {
    return getPartnerTableSortTimestamp(project.actualStartDate);
  }
  if (columnKey === 'actGolive') {
    return getPartnerTableSortTimestamp(project.actualGoliveDate);
  }
  if (columnKey === 'submitted') {
    return getPartnerTableSortTimestamp(project.submittedAt)
      ?? getMyProjectsColumnValue(project, columnKey);
  }
  return getMyProjectsColumnValue(project, columnKey);
};

export const getPaeQueueColumnValue = (project, columnKey) => {
  switch (columnKey) {
    case 'partner':
      return project.partnerName || '';
    case 'companyName':
      return project.companyName || '';
    case 'partnerServiceName':
      return project.partnerServiceName || '';
    case 'customer':
      return project.customerName || '';
    case 'products':
      return (project.productsInScope || []).join(' ');
    case 'engagement':
      return project.engagementType || '';
    case 'estStart':
    case 'start':
      return project.estimatedStartDate || '';
    case 'estGolive':
    case 'golive':
      return project.estimatedGoliveDate || '';
    case 'actStart':
      return project.actualStartDate || '';
    case 'actGolive':
      return project.actualGoliveDate || '';
    case 'status':
      return getPaeQueueDisplayStatus(project) || '';
    case 'pae':
      return project.partnerAdoptionEngineer || '';
    case 'region':
      return project.region || '';
    default:
      return '';
  }
};

export const getPaeQueueSortValue = (project, columnKey) => {
  if (columnKey === 'partnerServiceName') {
    return parsePartnerServiceNumber(project.partnerServiceName) ?? -1;
  }
  if (columnKey === 'estStart' || columnKey === 'start') {
    return getPartnerTableSortTimestamp(project.estimatedStartDate);
  }
  if (columnKey === 'estGolive' || columnKey === 'golive') {
    return getPartnerTableSortTimestamp(project.estimatedGoliveDate);
  }
  if (columnKey === 'actStart') {
    return getPartnerTableSortTimestamp(project.actualStartDate);
  }
  if (columnKey === 'actGolive') {
    return getPartnerTableSortTimestamp(project.actualGoliveDate);
  }
  return getPaeQueueColumnValue(project, columnKey);
};

export const getEmptyPartnerTableFilters = (columns) => (
  (columns || [])
    .filter((column) => column.filterable)
    .reduce((accumulator, column) => {
      accumulator[column.key] = column.filterType === 'multiSelect' ? [] : '';
      return accumulator;
    }, {})
);

export const buildPartnerTableFilterOptions = (projects, columns, getColumnValue) => {
  const filterableColumns = (columns || []).filter((column) => column.filterable);

  return filterableColumns.reduce((accumulator, column) => {
    if (column.filterOptionsSource === 'api') {
      accumulator[column.key] = [];
      return accumulator;
    }

    const uniqueValues = new Set();

    (projects || []).forEach((project) => {
      const rawValue = getColumnValue(project, column.key);
      const normalizedValue = String(rawValue || '').trim();
      if (normalizedValue) {
        uniqueValues.add(normalizedValue);
      }
    });

    accumulator[column.key] = Array.from(uniqueValues).sort((left, right) => (
      left.localeCompare(right, undefined, { sensitivity: 'base' })
    ));

    return accumulator;
  }, {});
};

export const buildPartnerTableStatusFilterOptions = (projects, getStatusValue) => {
  const statuses = new Set();

  (projects || []).forEach((project) => {
    const value = String(getStatusValue(project) || '').trim();
    if (value) {
      statuses.add(value);
    }
  });

  return Array.from(statuses).sort((left, right) => (
    left.localeCompare(right, undefined, { sensitivity: 'base' })
  ));
};

export const buildPartnerTableRegionFilterOptions = (projects, getColumnValue) => {
  const regions = new Set();

  (projects || []).forEach((project) => {
    const value = String(getColumnValue(project, 'region') || '').trim();
    if (value) {
      regions.add(value);
    }
  });

  return Array.from(regions).sort((left, right) => (
    left.localeCompare(right, undefined, { sensitivity: 'base' })
  ));
};

export const mergePartnerTableFilterOptions = (builtOptions, externalOptions = {}, appliedFilters = {}) => {
  const merged = { ...(builtOptions || {}) };

  Object.entries(externalOptions).forEach(([columnKey, options]) => {
    const appliedValues = normalizePartnerTableMultiFilterValue(appliedFilters[columnKey]);
    const uniqueValues = new Set(
      [...(options || []), ...appliedValues].filter((value) => String(value || '').trim()),
    );

    merged[columnKey] = Array.from(uniqueValues).sort((left, right) => (
      left.localeCompare(right, undefined, { sensitivity: 'base' })
    ));
  });

  return merged;
};

export const PAE_FILTER_COLUMN = {
  key: 'pae',
  label: 'PAE',
  sortable: false,
  filterable: true,
  filterOptionsSource: 'api',
};

export const REGION_FILTER_COLUMN = {
  key: 'region',
  label: 'Region',
  sortable: false,
  filterable: true,
  filterOptionsSource: 'api',
};

export const STATUS_FILTER_COLUMN = {
  key: 'status',
  label: 'Project Status',
  sortable: false,
  filterable: true,
  filterType: 'multiSelect',
  filterOptionsSource: 'api',
};

export const MY_PROJECTS_TABLE_COLUMNS = [
  { key: 'customer', label: 'Customer', sortable: true, filterable: true },
  { key: 'companyName', label: 'Partner Company', sortable: true, filterable: true },
  { key: 'products', label: 'Products', sortable: true, filterable: true },
  { key: 'engagement', label: 'Engagement', sortable: true, filterable: true },
  { key: 'status', label: 'Status', sortable: true, filterable: false },
  { key: 'estStart', label: 'Est Start', sortable: true, filterable: true, filterType: 'date' },
  { key: 'estGolive', label: 'Est Go-Live', sortable: true, filterable: true, filterType: 'date' },
  { key: 'actStart', label: 'Act Start', sortable: true, filterable: true, filterType: 'date' },
  { key: 'actGolive', label: 'Act Go-Live', sortable: true, filterable: true, filterType: 'date' },
  { key: 'submitted', label: 'Submitted', sortable: true, filterable: true },
];

export const MY_PROJECTS_FILTER_COLUMNS = [
  ...MY_PROJECTS_TABLE_COLUMNS.filter((column) => column.key !== 'status'),
  STATUS_FILTER_COLUMN,
  REGION_FILTER_COLUMN,
  PAE_FILTER_COLUMN,
];

export const PAE_QUEUE_TABLE_COLUMNS = [
  { key: 'partner', label: 'Partner Name', sortable: true, filterable: true },
  { key: 'companyName', label: 'Partner Company', sortable: true, filterable: true },
  { key: 'partnerServiceName', label: 'Partner Service', sortable: true, filterable: true },
  { key: 'customer', label: 'Customer', sortable: true, filterable: true },
  { key: 'products', label: 'Products', sortable: true, filterable: true },
  { key: 'engagement', label: 'Engagement', sortable: true, filterable: true },
  { key: 'status', label: 'Status', sortable: true, filterable: false },
  { key: 'estStart', label: 'Est Start', sortable: true, filterable: true, filterType: 'date' },
  { key: 'estGolive', label: 'Est Go-Live', sortable: true, filterable: true, filterType: 'date' },
  { key: 'actStart', label: 'Act Start', sortable: true, filterable: true, filterType: 'date' },
  { key: 'actGolive', label: 'Act Go-Live', sortable: true, filterable: true, filterType: 'date' },
];

export const PAE_QUEUE_FILTER_COLUMNS = [
  ...PAE_QUEUE_TABLE_COLUMNS.filter((column) => column.key !== 'status'),
  STATUS_FILTER_COLUMN,
  REGION_FILTER_COLUMN,
  PAE_FILTER_COLUMN,
];
